import {
  REWARD_CASH, REWARD_FREESPIN, bonusTiers, isAutoBonus, tierKind, tierRangeText,
} from './bonusRules';

/** Bonus kartlarında gösterilecek birincil ödül bilgisini tekilleştirir. */

export const isFreespinOnlyBonus = (bonus) => (
  bonus?.cash_reward_enabled === false && !!bonus?.freespin_enabled
);

/** Otomatik tanımlanan, bakiyeyi kademe tablosundan veren kampanya. */
export const isCashTierBonus = (bonus) => (
  isAutoBonus(bonus) && tierKind(bonus) === REWARD_CASH && bonusTiers(bonus).length > 0
);

export const freespinRewardText = (bonus, fallbackCount = 0) => {
  // Kademeli kampanyada tek bir adet yoktur; aralık yazılır.
  const range = tierKind(bonus) === REWARD_FREESPIN ? tierRangeText(bonus) : '';
  if (range) return range;
  const count = Number(bonus?.freespin_count) || Number(fallbackCount) || 0;
  return count > 0 ? `${count} Freespin` : 'Freespin';
};

/** Kart görselinin üstüne basılan ödül başlığı; boş string = hiç çizilmez.
 *
 * Yüzdeli kampanyada **bilinçli olarak boştur**: oranı banner görselinin
 * kendi tasarımı söyler ("4 Yatırım Yap, 5. Bizden" gibi), üstüne ikinci bir
 * "%100" basmak tasarımı bozuyordu — yüzdesi tanımsız kampanyalarda ise
 * kartta kocaman bir "%0" görünüyordu. Değerin kendisi kaybolmaz: hesap
 * `bonus_percent` üzerinden yapılır ve panel onu ``adminBonusHeadline`` ile
 * göstermeye devam eder.
 */
export const bonusRewardHeadline = (bonus, fallbackCount = 0) => {
  if (isFreespinOnlyBonus(bonus)) {
    return freespinRewardText(bonus, fallbackCount);
  }
  // Kademeli bakiye kampanyası yüzdeyle değil tabloyla ödüllendirir;
  // başlık freespin kampanyasındaki gibi aralığı yazar.
  if (isCashTierBonus(bonus)) return tierRangeText(bonus);
  return bonus?.is_trial_bonus
    ? `${Number(bonus?.trial_amount || 0)} ₺`
    : '';
};

/** Paneldeki liste kartının başlığı: yönetici oranı burada görmeye devam eder. */
export const adminBonusHeadline = (bonus, fallbackCount = 0) => (
  bonusRewardHeadline(bonus, fallbackCount)
  || `%${Number(bonus?.bonus_percent || 0)}`
);

export const bonusPrimaryMetric = (bonus, fallbackCount = 0) => {
  if (isFreespinOnlyBonus(bonus)) {
    return {
      label: 'Freespin Ödülü',
      value: freespinRewardText(bonus, fallbackCount),
    };
  }
  if (isCashTierBonus(bonus)) {
    return { label: 'Bonus Ödülü', value: tierRangeText(bonus) };
  }
  return bonus?.is_trial_bonus
    ? {
      label: 'Bonus Tutarı',
      value: `${Number(bonus?.trial_amount || 0)} ₺`,
    }
    : {
      label: 'Min. Yatırım',
      value: `${Number(bonus?.min_deposit || 0)} ₺`,
    };
};

/** Yalnız para ödülü açık deneme kampanyasında sabit tutar zorunludur. */
export const requiresTrialCashAmount = (bonus) => (
  !!bonus?.is_trial_bonus && bonus?.cash_reward_enabled !== false
);

// Ödülün gerçekten alındığı sayfalar. Düğme etiketi adresten türetilir ki
// yönetici yalnızca adresi seçsin; tanımadığımız yolda genel etiket kullanılır.
const REDIRECT_LABELS = {
  '/cark': 'Çarkı Çevir',
  '/capa': 'Çapayı At',
};

/** Kampanya kartındaki eylem düğmesinin etiketi ve davranışı.
 *
 * Üç talep yolu vardır ve ikisinde bonus siteden **hiç** alınmaz (talep ucu da
 * kapalıdır): ``claim_via_support`` canlı destek penceresini açar,
 * ``claim_redirect_url`` ise oyuncuyu ödülün alındığı sayfaya götürür (şans
 * çarkı, çapa). Kilit ve günlük hak sayaçları bu iki kampanyada anlamsızdır —
 * talep kaydı oluşmaz. Etiketler tek yerde durur; kart ile detay penceresi
 * aynı şeyi söylemelidir.
 *
 * ``chatState`` ``liveChatConfig.liveChatState`` sonucudur ('off' =
 * canlı destek kapalı, düğme tıklanamaz).
 */
export const bonusClaimAction = (bonus, options = {}) => {
  const { locked = false, chatState = 'ready' } = options;
  // Otomatik kampanyada talep yoktur: hak yatırım onaylanınca arka planda
  // verilir, düğme oyuncuyu yatırıma götürür (talep ucu da kapalıdır).
  if (isAutoBonus(bonus)) {
    return {
      mode: 'auto',
      label: 'Yatırım Yap',
      href: '/hesap/para-yatir',
      disabled: false,
    };
  }
  const redirect = (bonus?.claim_redirect_url || '').trim();
  if (redirect) {
    return {
      mode: 'redirect',
      label: REDIRECT_LABELS[redirect] || 'Kampanyaya Git',
      href: redirect,
      disabled: false,
    };
  }
  if (bonus?.claim_via_support) {
    return {
      mode: 'support',
      label: 'Canlı Destekten Talep Et',
      disabled: chatState === 'off',
    };
  }
  if (locked) {
    return {
      mode: 'locked',
      label: Number(bonus?.daily_claim_limit) > 0
        ? 'Günlük hakkın doldu'
        : 'Bonus Alındı',
      disabled: true,
    };
  }
  if (bonus?.claim_state === 'first_deposit_required') {
    return { mode: 'deposit', label: 'İlk Yatırımını Yap', disabled: false };
  }
  return { mode: 'claim', label: 'Bonus Al', disabled: false };
};
